import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { computed, ref } from 'vue';
import LlConfirmationDialog from '@/components/LlConfirmationDialog.vue';
import LlRadioGroup from '@/components/LlRadioGroup.vue';
import { WipDisposition } from '../constants';

/**
 * UI-008 (one fee) and UI-009 (a bulk selection) are the same prompt with
 * different copy, so they are one component: the only differences are the
 * sentence and the plural.
 *
 * The choice is mandatory (BR-012) — Confirm stays disabled until one is picked,
 * and there is no default. Cancelling resolves null and the caller makes no
 * request at all, leaving every fee Billed and Time Unlocked.
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'FixedFeeWipDispositionDialog',
  setup(__props, { expose: __expose }) {

const dialogRef = ref<{ open: () => Promise<boolean> } | null>(null);
const choice = ref<string | null>(null);
const feeCount = ref(0);
const entryCount = ref(0);

const isBulk = computed(() => feeCount.value > 1);

const message = computed(() => {
  const entries = `${entryCount.value} time ${
    entryCount.value === 1 ? 'entry' : 'entries'
  } not yet billed`;
  // UI-009 states the consequence first, then the aggregate (frd.md §3.3) — a
  // bulk action touching specific entries has to say why before it asks what.
  return isBulk.value
    ? `Locking time on these ${feeCount.value} fees will affect time entries that aren't yet billed. Together they have ${entries}.`
    : `This fee has ${entries}.`;
});

const options = computed(() => [
  {
    label: isBulk.value ? 'Mark them all Billed now' : 'Mark them Billed now',
    value: WipDisposition.MarkBilled,
    // The irreversibility line is required by the story and must not be
    // simplified away: these entries stay Billed through a later Unlock Time.
    description:
      "Once billed, these entries can't be moved to another fee — including if you unlock this fee later.",
    descriptionClass: 'text-error',
    dataCy: 'wip-mark-billed',
  },
  {
    label: isBulk.value
      ? 'Leave them all in the workflow'
      : 'Leave them in the workflow',
    value: WipDisposition.LeaveInWorkflow,
    description:
      'They stay unbilled and appear on Ready to Bill by their own entry date.',
    dataCy: 'wip-leave-in-workflow',
  },
]);

/** Resolves the chosen disposition, or null when cancelled. */
async function open(counts: { feeCount: number; entryCount: number }) {
  feeCount.value = counts.feeCount;
  entryCount.value = counts.entryCount;
  choice.value = null;

  try {
    const confirmed = await dialogRef.value?.open();
    return confirmed ? choice.value : null;
  } catch {
    // Treated as a cancel: no disposition means the caller makes no request,
    // which is the safe end of a prompt whose whole job is to be answered.
    return null;
  }
}

__expose({ open });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(LlConfirmationDialog, {
    ref_key: "dialogRef",
    ref: dialogRef,
    title: "Choose what should be done with the unbilled time entries",
    message: message.value,
    "confirm-text": "Lock time",
    "confirm-color": "primary",
    "confirm-variant": "flat",
    "max-width": 600,
    persistent: "",
    "confirm-disabled": !choice.value,
    "data-cy": "wip-disposition-dialog"
  }, {
    content: _withCtx(() => [
      _createVNode(LlRadioGroup, {
        modelValue: choice.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((choice).value = $event)),
        options: options.value,
        cards: "",
        "data-cy": "wip-disposition-choice"
      }, null, 8, ["modelValue", "options"])
    ]),
    _: 1
  }, 8, ["message", "confirm-disabled"]))
}
}

})